import { useCallback, useEffect, useState } from 'react';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { LabReportDetail } from '@/components/lab/LabReportDetail';
import { ApiCallError, callFunction } from '@/lib/api';
import { formatLabDateTime } from '@/lib/lab/labDisplay';
import type {
  LabDetail,
  LabPartyRole,
  LabRegisteredParty,
  LabRegisteredPartyReport,
} from '@/lib/lab/labTypes';
import { patientTabPath } from '@/lib/patientRecordPaths';

function roleLabel(role: LabPartyRole): string {
  switch (role) {
    case 'sender':
      return 'Afsender';
    case 'receiver':
      return 'Rekvirent';
    case 'cc_receiver':
      return 'Kopimodtager';
    default: {
      const _never: never = role;
      return _never;
    }
  }
}

function pairingLabel(status: LabRegisteredParty['pairing_status']): string {
  switch (status) {
    case 'paired':
      return 'Parret';
    case 'created':
      return 'Oprettet';
    default: {
      const _never: never = status;
      return _never;
    }
  }
}

function reportCaption(report: LabRegisteredPartyReport): string {
  const when = formatLabDateTime(report.results_datetime ?? report.received_at);
  return `${report.kind} · brev ${report.letter_identifier || '—'} · ${when}`;
}

export function LabPartiesPanel({
  clinicId,
  refreshKey,
}: {
  clinicId: string;
  refreshKey?: number;
}) {
  const [parties, setParties] = useState<LabRegisteredParty[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<LabDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await callFunction<Record<string, unknown>, { parties: LabRegisteredParty[] }>(
        'communicate-lab-import',
        { action: 'list-parties', clinic_id: clinicId },
      );
      setParties(result.parties);
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Labparter kunne ikke hentes.');
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  async function openReport(reportId: string) {
    if (openId === reportId) {
      setOpenId(null);
      setDetail(null);
      setDetailError(null);
      return;
    }
    setOpenId(reportId);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const result = await callFunction<Record<string, unknown>, LabDetail>('communicate-lab-import', {
        action: 'detail',
        clinic_id: clinicId,
        report_id: reportId,
      });
      setDetail(result);
    } catch (err) {
      setDetailError(err instanceof ApiCallError ? err.message : 'Laboratoriesvaret kunne ikke åbnes.');
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <section className="space-y-3 rounded-md border border-ds-line p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Afsender- og kopimodtagerregister</h3>
        <Button type="button" variant="secondary" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? 'Opdaterer…' : 'Opdatér'}
        </Button>
      </div>
      <p className="text-sm text-ds-muted">
        Ukendte afsendere og kopimodtagere oprettes automatisk ved import. Parres på EAN, hvis det matcher
        klinikkens Communicate-EAN eller en afsenderidentitet. Clinio har ingen særskilt rekvirent- eller
        klinikklassifikation.
      </p>
      {error && (
        <p role="alert" className="text-sm text-ds-red-ink">
          {error}
        </p>
      )}
      {parties === null && loading && (
        <div className="flex flex-col gap-s3" aria-busy>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}
      {parties && parties.length === 0 && !loading && (
        <p className="text-sm text-ds-muted">Ingen labparter endnu. Indlæs et XRPT01- eller XRPT05-svar.</p>
      )}
      {parties && parties.length > 0 && (
        <ul className="space-y-3">
          {parties.map((party) => {
            const sent = party.reports.filter((r) => r.role === 'sender');
            const other = party.reports.filter((r) => r.role !== 'sender');
            return (
              <li key={party.id} className="rounded-md border border-ds-line p-3 text-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-medium">
                    {party.organisation_name || 'Ukendt organisation'}
                    {party.department_name ? ` · ${party.department_name}` : ''}
                    {party.unit_name ? ` · ${party.unit_name}` : ''}
                  </p>
                  <span className={party.pairing_status === 'paired' ? 'lab-chip lab-chip--copy' : 'lab-chip'}>
                    {pairingLabel(party.pairing_status)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-ds-muted">
                  {party.ean ? `EAN ${party.ean}` : 'Ingen EAN'}
                  {party.identifier
                    ? ` · ID ${party.identifier}${party.identifier_code ? ` (${party.identifier_code})` : ''}`
                    : ''}
                </p>
                <p className="mt-2 text-ds-text">{party.pairing_detail}</p>
                {sent.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-ds-muted">Svar de har sendt</p>
                    <ul className="mt-1 space-y-1">
                      {sent.map((report) => (
                        <li key={`${report.id}-${report.role}`} className="flex flex-wrap items-baseline gap-s2">
                          <Button type="button" variant="link" size="sm" onClick={() => void openReport(report.id)}>
                            {reportCaption(report)}
                          </Button>
                          {report.patient_id ? (
                            <ButtonLink
                              to={patientTabPath(report.patient_id, 'lab_results')}
                              variant="link"
                              size="sm"
                            >
                              {report.patient_name || 'Patient'}
                            </ButtonLink>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {other.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-ds-muted">Indgår i svar</p>
                    <ul className="mt-1 space-y-1">
                      {other.map((report) => (
                        <li key={`${report.id}-${report.role}`} className="flex flex-wrap items-baseline gap-s2">
                          <span className="text-xs text-ds-muted">{roleLabel(report.role)}</span>
                          <Button type="button" variant="link" size="sm" onClick={() => void openReport(report.id)}>
                            {reportCaption(report)}
                          </Button>
                          {report.patient_id ? (
                            <ButtonLink
                              to={patientTabPath(report.patient_id, 'lab_results')}
                              variant="link"
                              size="sm"
                            >
                              {report.patient_name || 'Patient'}
                            </ButtonLink>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {openId && party.reports.some((r) => r.id === openId) ? (
                  <div className="mt-s3">
                    {detailLoading && <p className="text-sm text-ds-muted">Åbner svaret…</p>}
                    {detailError && (
                      <p role="alert" className="text-sm text-ds-red-ink">
                        {detailError}
                      </p>
                    )}
                    {detail && detail.report.id === openId ? (
                      <LabReportDetail
                        report={detail.report}
                        data={detail}
                        revealedCpr={null}
                        clinicId={clinicId}
                      />
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
